import { useEffect, useRef, useState, useCallback } from 'react';
import { Eraser } from 'lucide-react';

// Canvas de signature au doigt : trait lissé (courbes quadratiques), HiDPI, touch-action none.
export function SignaturePad({ onChange, disabled }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef(null);
  const [hasInk, setHasInk] = useState(false);

  const setup = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ratio = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * ratio;
    canvas.height = rect.height * ratio;
    const ctx = canvas.getContext('2d');
    ctx.scale(ratio, ratio);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineWidth = 2.6;
    ctx.strokeStyle = '#0f172a';
  }, []);

  useEffect(() => { setup(); window.addEventListener('resize', setup); return () => window.removeEventListener('resize', setup); }, [setup]);

  const pos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const start = (e) => {
    if (disabled) return;
    e.preventDefault();
    canvasRef.current.setPointerCapture?.(e.pointerId);
    drawing.current = true;
    last.current = pos(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.beginPath();
    ctx.arc(last.current.x, last.current.y, 1.2, 0, Math.PI * 2);
    ctx.fill();
  };

  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const p = pos(e);
    const ctx = canvasRef.current.getContext('2d');
    const mid = { x: (last.current.x + p.x) / 2, y: (last.current.y + p.y) / 2 };
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.quadraticCurveTo(last.current.x, last.current.y, mid.x, mid.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
    if (!hasInk) { setHasInk(true); }
  };

  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    onChange?.(canvasRef.current.toDataURL('image/png'));
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.save(); ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.clearRect(0, 0, canvas.width, canvas.height); ctx.restore();
    setHasInk(false);
    onChange?.(null);
  };

  return (
    <div className="space-y-2" data-testid="signature-pad">
      <div className={`relative rounded-xl border-2 ${hasInk ? 'border-[#0A3D73]' : 'border-dashed border-slate-300'} bg-white transition-colors duration-200 overflow-hidden`}>
        <canvas
          ref={canvasRef}
          className="block w-full h-48 sm:h-56 touch-none cursor-crosshair select-none"
          style={{ touchAction: 'none' }}
          onPointerDown={start}
          onPointerMove={move}
          onPointerUp={end}
          onPointerCancel={end}
          onPointerLeave={end}
          data-testid="signature-canvas"
        />
        <div className="pointer-events-none absolute left-6 right-6 bottom-10 border-b border-slate-200" />
        {!hasInk && (
          <p className="pointer-events-none absolute inset-x-0 bottom-3 text-center text-xs text-slate-400 select-none">Signez ici avec le doigt ou la souris</p>
        )}
      </div>
      <div className="flex justify-end">
        <button type="button" onClick={clear} disabled={!hasInk} className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-600 hover:text-red-600 disabled:opacity-40 disabled:hover:text-slate-600 transition-colors" data-testid="signature-clear-btn">
          <Eraser className="w-3.5 h-3.5" /> Effacer
        </button>
      </div>
    </div>
  );
}
